import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import UserProfileCard from "../../components/UserProfileCard";
import { FolderOpen, LayoutGrid } from "lucide-react";
import ReportListItem, {
  type ReportItem,
} from "../../components/Reports/ReportListItem";
import ReportSortBar, {
  type SortOptionValue,
} from "../../components/Reports/ReportSortBar";
import Pagination from "../../components/UI/Pagination";
import { getReportsProjects, uploadReportFile } from "../../api/reports/reports";
import { validateReportFile } from "../../api/upload/file";
import type { Pagination as PaginationType } from "../../utils/interface/pagination";
import { extractApiError } from "../../utils/util";
import defaultProjectImg from "../../assets/Projects/project1.png";
import { useAuth } from "../../contexts/AuthContext";

function formatCompletedDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** Map API report/project item to ReportItem. Report URL from nested report. Completed date from report.createdAt when report exists. */
function getReportFileUrl(item: {
  report?: { fileUrl?: string; reportFileUrl?: string; url?: string; reportUrl?: string; documentUrl?: string; createdAt?: string };
  reportFileUrl?: string;
  reportUrl?: string;
  fileUrl?: string;
}): string | undefined {
  const r = item.report;
  if (r && (r.fileUrl || r.reportFileUrl || r.url || r.reportUrl || r.documentUrl)) {
    return r.fileUrl || r.reportFileUrl || r.url || r.reportUrl || r.documentUrl;
  }
  return item.reportFileUrl || item.reportUrl || item.fileUrl;
}

function mapToReportItem(item: {
  id: string;
  name?: string;
  title?: string;
  summary?: string;
  description?: string;
  completedDate?: string;
  updatedAt?: string;
  createdAt?: string;
  image?: string;
  projectThumbnail?: string;
  projectThumbnailPreview?: string;
  thumbnail?: string;
  reportFileUrl?: string;
  reportUrl?: string;
  fileUrl?: string;
  report?: { id?: string; projectId?: string; fileUrl?: string; reportFileUrl?: string; url?: string; reportUrl?: string; documentUrl?: string; createdAt?: string };
}): ReportItem {
  const report = item.report;
  const dateRaw = report?.createdAt ?? "";
  const reportUrl = getReportFileUrl(item);
  const thumbnailDisplay = item.projectThumbnailPreview ?? item.projectThumbnail;
  return {
    id: item.id,
    title: item.title ?? item.name ?? "",
    description: item.description ?? item.summary ?? "",
    completedDate: dateRaw ? formatCompletedDate(dateRaw) : "",
    image:
      item.image ??
      thumbnailDisplay ??
      item.thumbnail ??
      defaultProjectImg,
    reportFileUrl: reportUrl,
  };
}

const Reports = () => {
  const { userDetails } = useAuth();
  const [sortValue, setSortValue] = useState<SortOptionValue>("date-desc");
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [uploadingReportId, setUploadingReportId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pagination, setPagination] = useState<PaginationType | null>(null);

  const PAGE_SIZE = 5;

  const sortOrderParam: "ASC" | "DESC" =
    sortValue === "date-asc" ? "ASC" : "DESC";

  const fetchReports = useCallback(
    async (page: number) => {
      setLoading(true);
      try {
        const resp = await getReportsProjects(page, PAGE_SIZE, sortOrderParam);
        const rawProjects = resp?.projects ?? [];
        setReports(rawProjects.map(mapToReportItem));
        setPagination(resp?.pagination ?? null);
      } catch (error) {
        setReports([]);
        setPagination(null);
        toast.error(extractApiError(error));
      } finally {
        setLoading(false);
      }
    },
    [sortOrderParam],
  );

  const handleUploadReport = useCallback(
    async (report: ReportItem, file: File) => {
      const validationError = validateReportFile(file);
      if (validationError) {
        toast.error(validationError);
        return;
      }
      setUploadingReportId(report.id);
      try {
        await uploadReportFile(report.id, file);
        await fetchReports(currentPage);
      } catch (error) {
        toast.error(extractApiError(error));
      } finally {
        setUploadingReportId(null);
      }
    },
    [currentPage, fetchReports],
  );

  useEffect(() => {
    fetchReports(currentPage);
  }, [currentPage, fetchReports]);

  return (
    <div>
      <UserProfileCard />

      <div className="heading-wrap mt-2">
        <h1 className="text-[34px] lg:text-[38px] xl:text-[56px] font-display font-weight-300 text-white mb-2">
          Reports
        </h1>
        <p className="text-white font-weight-300 text-[14px] lg:text-[16px] xl:text-[18px]">
          {userDetails?.type === "2"
            ? "Download reports from any previously completed IFC file uploads"
            : "Download reports from any previously completed IFC file uploads"}
        </p>
      </div>

      <div className="mt-6 mb-4">
        <ReportSortBar
          value={sortValue}
          onChange={(v) => {
            setSortValue(v);
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="downoad-project overflow-hidden ">
        {loading && (
          <div className="flex flex-col items-center justify-center">
            <div className="relative">
              <div className="w-20 h-20 border-[3px] border-[#E761A0]/20 border-t-[#E761A0] rounded-full animate-spin" />
              <LayoutGrid className="w-8 h-8 text-[#E761A0] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <p className="text-white/90 text-base mt-5 font-medium">
              Loading reports...
            </p>
          </div>
        )}
        {!loading && reports.length === 0 && (
          <div className="text-center py-20 px-6">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl border border-[#E761A0]/30 bg-[#E761A0]/5 mb-6">
              <FolderOpen className="w-12 h-12 text-[#E761A0]" />
            </div>
            <h3 className="text-white text-lg font-semibold mb-2">
              No reports to show
            </h3>

          </div>
        )}
        {!loading &&
          reports.length > 0 &&
          reports.map((report, i) => (
            <ReportListItem
              key={report.id}
              report={report}
              isLast={i === reports.length - 1}
              userType={userDetails?.type != null ? String(userDetails.type) : undefined}
              showDownloadForUser={true}
              onUpload={handleUploadReport}
              uploadLoading={uploadingReportId === report.id}
              buttonLabel="DOWNLOAD REPORT"
              uploadButtonLabel="UPLOAD REPORT"
            />
          ))}
        {!loading && pagination && pagination.totalPages > 1 && (() => {
          const totalCount = pagination.totalItems ?? pagination.currentlyShowing;
          const showingFrom = Math.min((currentPage - 1) * pagination.limit + 1, totalCount);
          const showingTo = Math.min(currentPage * pagination.limit, totalCount);
          return (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.totalPages}
                onPageChange={setCurrentPage}
                totalItems={totalCount}
                itemsPerPage={pagination.limit}
                showingFrom={showingFrom}
                showingTo={showingTo}
              />
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default Reports;
